import React, { createContext, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  acceptTerms as apiAcceptTerms,
  API_EVENT_UNAUTHORIZED,
  apiEventEmitter,
  getMe as apiGetMe,
  loginWithPhone as apiLoginWithPhone,
  loginWithUsernamePassword as apiLoginWithUsernamePassword,
  register,
  createPayment as apiCreatePayment,
  logout as apiLogout,
  sendFirebaseToken as apiSendFirebaseToken,
  updateMe,
  verifyPhone as apiVerifyPhone,
  getCode,
  getProducts,
  getMembershipDetails as apiGetMembershipDetails,
  updateStripeCard,
  cancelMembership,
  resumeMembership,
  retrieveUpgradePreview,
  removeSavedPaymentMethod,
  generatePasswordReset,
  confirmPasswordReset as apiConfirmPasswordReset,
  storeLockedMemberNotifyPreferences as apiStoreLockedMemberNotifyPreferences,
  loginWithAutoLoginToken as apiLoginWithAutoLoginToken,
  UpdateStripeCardResponse,
  upgradeMembership,
} from "../api/client";

import UserProfile from "../api/entities/UserProfile";
import AuthTokenResponse from "../api/responses/AuthTokenResponse";
import ProductsResponse from "../api/responses/ProductsResponse";
import UpdateUserRequest from "../api/requests/UpdateUserRequest";
import SignUpTrialRequest from "../api/requests/SignUpTrialRequest";
import UpdatePhoneRequest from "../api/requests/UpdatePhoneRequest";
import { useStripe } from "@stripe/react-stripe-js";
import CancelSubscriptionRequest from "../api/requests/CancelMembershipRequest";
import ConfirmPasswordResetRequest from "../api/requests/ConfirmPasswordResetRequest";
import StoreLockedMemberNotifyRequest from "../api/requests/StoreLockedMemberNotifyRequest";
import CreatePaymentRequest from "../api/requests/CreatePaymentRequest";
import { buildDeviceDetails, getUniqueId } from "../utils/device";
import { DuplicateEmailError, ValidationError } from "../api/errors";
import {
  getUserMessage,
  getPhoneValidationSuggestion,
  PhoneValidationSuggestion,
} from "../utils/errorMessages";
import EventEmitter from "eventemitter3";
import { MembershipDetails, Payment } from "@booyaltd/core";
import { LogEventCategory } from "../api/entities/Log";
import {
  markSessionStored,
  queueWebLogoutEvent,
} from "../utils/webLogoutTelemetry";

export const LOGIN_EVENT = "login-event";

export const loginEventEmitter = new EventEmitter();

export type SendMethod = "sms" | "whatsapp";

export interface IAccountContext {
  loggedIn: boolean;
  isLocked: boolean;
  loginError: string | undefined;
  phoneSuggestion: PhoneValidationSuggestion | undefined;
  resetPhoneSuggestion(): void;
  loading: boolean;
  validated: boolean;
  waitingOnVerificationCode: boolean;
  userProfile?: UserProfile;

  storeAuthResponse(response: AuthTokenResponse): Promise<any>;

  loginWithUsernamePassword(userName: string, password: string): Promise<any>;

  loginWithPhone(
    countryCallingCode: string,
    phone: string,
    method: SendMethod,
  ): Promise<any>;

  loginWithPhoneVerificationToken(token: string): Promise<any>;

  loginWithAutoLoginToken(token: string): Promise<any>;

  logout(): void;

  validateLogin(): Promise<UserProfile | null>;

  acceptTerms(): Promise<boolean>;

  sendFirebaseToken(token: string): Promise<boolean>;

  resetLoginWithPhone(): void;

  resetErrors(): void;

  resetRetryPaymentErrors(): void;

  update(req: UpdateUserRequest): Promise<boolean>;

  getVerificationCode(req: UpdatePhoneRequest): void;

  sendPasswordResetEmail(email: string): Promise<void>;

  confirmPasswordReset(req: ConfirmPasswordResetRequest): Promise<void>;

  updating: boolean;
  updateError: string | undefined;

  signUpTrial(req: SignUpTrialRequest): Promise<any>;

  // #1225: both resolve to the trial payment's ID from the POST /payments reply.
  // Callers send the StartTrial pixel event as `start_trial:payment:<id>`, the
  // event ID the server's Facebook listener uses for the same trial.
  signUpTrialVerificationToken(code: string): Promise<string | undefined>;

  assignTrialMembership(): Promise<string | undefined>;

  getMembershipDetails(): Promise<MembershipDetails>;

  handleCardSubmit(paymentMethodId: string): Promise<UpdateStripeCardResponse>;

  handleRetryPaymentStatus(currentFailedPaymentId: string): Promise<any>;

  handleCancelMembership(): void;

  handleResumeMembership(): void;

  getUpgradePreview(id: string): Promise<any>;

  handleUpgradeMembership(id: string): void;

  handleRemovePaymentMethod(id: string): void;

  updateCard(value: boolean): void;

  updatingCard: boolean;

  getSubscriptionProducts(): Promise<any>;

  createStandardSubscriptionStripe(
    req: CreatePaymentRequest,
    onPaymentSuccess?: (payment: Payment) => void,
  ): Promise<any>;

  subscriptionProducts?: ProductsResponse;
  paymentError: boolean;
  paymentErrorResponse: string | undefined;

  retryPaymentStatus: string | undefined;

  setRetryPaymentStatus(status: string): void;

  retryPaymentFailureResponse: string | undefined;

  loginPhoneCountryCode: string | undefined;
  loginPhoneNumber: string | undefined;

  upgradePreviewDetails?: any | undefined;

  notifyLockedMember(req: StoreLockedMemberNotifyRequest): Promise<any>;
}

export const AUTH_TOKEN_STORAGE_KEY = "ff-at";
export const REFRESH_TOKEN_STORAGE_KEY = "ff-rt";
export const UNIQUE_ID_STORAGE_KEY = "ff-uid";

// WS-01 (#856, #824): localStorage is shared by every tab of the origin, so
// the session in storage may belong to another tab or to a newer login flow.
// A session is identified by its refresh token: the 401 handler drops and
// rotates ff-at within one session, but ff-rt only changes when a session is
// replaced (login, sign-out, successful refresh).
export const storedRefreshToken = (): string | null =>
  localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);

// True when storage still holds the session identified by `ownedRefreshToken`
// (null = "no session"), i.e. the caller may end it.
export const isSessionOwnedBy = (ownedRefreshToken: string | null) =>
  storedRefreshToken() === ownedRefreshToken;

// #990: the member number inside a FarmFlix token (a JWT: its middle part is
// base64url JSON carrying userId), or undefined if it can't be read. The
// signature is NOT checked, so the result is used for one thing only: whether
// a refused handoff may keep the stored session. That can only keep a session
// already stored for that member; it never signs anyone in.
export const tokenMemberId = (token: string | null): number | undefined => {
  if (!token) return undefined;
  try {
    const payload = token.split(".")[1];
    if (!payload) return undefined;
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const { userId } = JSON.parse(atob(padded));
    return typeof userId === "number" ? userId : undefined;
  } catch (e) {
    // The error's name only: a JSON.parse message can quote the token.
    console.log(
      "[web-login] could not read the member from a token:",
      e instanceof Error ? e.name : typeof e,
    );
    return undefined;
  }
};

const AccountContext = createContext<IAccountContext>({
  loggedIn: false,
} as IAccountContext);

const AccountProvider = (props: any) => {
  const [userProfile, setUserProfile] = useState<UserProfile>();
  const [loggedIn, setLoggedIn] = useState(false);
  const [validated, setValidated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string>();
  const [loginError, setLoginError] = useState<string>();
  const [phoneSuggestion, setPhoneSuggestion] = useState<
    PhoneValidationSuggestion | undefined
  >();
  const [paymentError, setPaymentError] = useState(false);
  // eslint-disable-next-line
  const [paymentErrorResponse, setPaymentErrorResponse] = useState<string>();

  const [subscriptionProducts, setSubscriptionProducts] =
    useState<ProductsResponse>();
  const [upgradePreviewDetails, setUpgradePreviewDetails] = useState<any>();
  const [waitingOnVerificationCode, setWaitingOnVerificationCode] =
    useState(false);

  const [updatingCard, setUpdatingCard] = useState(false);
  const [loginPhoneCountryCode, setLoginPhoneCountryCode] = useState<string>();
  const [loginPhoneNumber, setLoginPhoneNumber] = useState<string>();

  // Retry Payment
  const [retryPaymentStatus, setRetryPaymentStatus] = useState("");
  const [retryPaymentFailureResponse, setRetryPaymentFailureResponse] =
    useState<string>();

  const stripe = useStripe();
  const navigate = useNavigate();

  // Returns the stored refresh token — the id of the session this caller now
  // owns (see endOwnedSession).
  const storeAuthResponse = async (response: AuthTokenResponse) => {
    localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, response.authToken);
    localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, response.refreshToken);
    markSessionStored();
    return response.refreshToken;
  };

  // WS-01 (#856, #824): the login flows no longer call logout() on entry,
  // and their catch only ends a session the flow itself stored. A blind
  // logout() removed the tab-shared keys, so a mistyped password in one tab
  // signed out a live session in another (#824), and any stray call after a
  // handoff login left the handoff tab with no keys (#856 / WS-12).
  // #327 is kept: the handoff flow (loginWithAutoLoginToken) clears the old
  // session before storing the new one once the API accepts the link, and on
  // a refused link clears the stored session unless it belongs to the member
  // the link names (#990: no longer cleared on entry, so a stale link reopened
  // in the browser can't sign that member out); the member's own Log out
  // still removes both keys; and the 401 handler no longer writes a refreshed
  // old session over a newer one (client.ts handleUnauthorizedResponse).

  const loginWithUsernamePassword = async (
    username: string,
    password: string,
  ) => {
    resetErrors();
    let ownRefreshToken: string | undefined;

    try {
      setLoading(true);
      const response = await apiLoginWithUsernamePassword({
        username,
        password,
        device: await buildDeviceDetails(),
      });
      ownRefreshToken = await storeAuthResponse(response);
      const userProfile = await validateLogin();
      loginEventEmitter.emit(LOGIN_EVENT, "email", userProfile);
      // window.location.reload();
    } catch (e) {
      console.log("[web-login] email/password login failed:", String(e));
      setLoginError(
        typeof e === "string" || e instanceof ValidationError
          ? e.toString()
          : "Invalid email / password",
      );
      if (ownRefreshToken !== undefined) {
        await endOwnedSession(ownRefreshToken, "login-email-failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const createStandardSubscriptionStripe = async (
    { productId, stripePaymentToken, eventId }: CreatePaymentRequest,
    onPaymentSuccess?: (payment: Payment) => void,
  ) => {
    try {
      // Create subscription for the member
      await apiCreatePayment({
        productId: productId,
        stripePaymentToken: stripePaymentToken,
        eventId: eventId,
      });
      // If card error = 400
      // If card successfull = 402
    } catch (e) {
      if (typeof e === "string" || e instanceof ValidationError) {
        return { status: "payment_error", message: e.toString() };
      }

      // Only the Stripe "requires further action" shape carries a
      // paymentProviderRawPayload. Guard every hop with optional chaining so
      // a NoConnectionError / bare Error can't throw a secondary TypeError
      // inside the catch and mask the real failure (#569).
      const rawPayload =
        e && typeof e === "object"
          ? // @ts-ignore - defensive read of an unknown error shape
            e.response?.payment?.paymentProviderRawPayload
          : undefined;

      if (rawPayload) {
        return { status: "payment_pending", message: rawPayload };
      }

      return {
        status: "payment_error",
        message:
          "Something went wrong processing your payment. Please try again.",
      };
    } finally {
    }
  };

  /* ===================================
  ======= END Trial Applications =======
  =================================== */

  const loginWithPhone = async (
    countryCode: string,
    phone: string,
    method: SendMethod,
  ) => {
    resetErrors();
    // WS-01: no entry/catch logout() — sending a code stores no session, so
    // there is nothing of this flow's to end (WS-01 note above
    // loginWithUsernamePassword).

    setLoginPhoneCountryCode(countryCode);
    setLoginPhoneNumber(phone);

    try {
      setLoading(true);
      const success = await apiLoginWithPhone({ countryCode, phone, method });
      if (!success) {
        throw new Error("Unauthorized");
      }

      setWaitingOnVerificationCode(true);
    } catch (e) {
      console.log("[web-login] phone login (send code) failed:", String(e));
      // getUserMessage handles structured #284 phone-validation payloads
      // (extracts userMessage / error / message from the response body)
      // as well as plain ValidationError strings.
      setLoginError(getUserMessage(e));
      const suggestion = getPhoneValidationSuggestion(e);
      if (suggestion) setPhoneSuggestion(suggestion);
    } finally {
      setLoading(false);
    }
  };

  const notifyLockedMember = async (req: StoreLockedMemberNotifyRequest) => {
    try {
      setLoading(true);
      // eslint-disable-next-line
      const response = await apiStoreLockedMemberNotifyPreferences({
        userId: req.userId,
        notificationPreference: req.notificationPreference,
      });
    } catch (e) {
      console.log("Error:" + e);
    } finally {
      setLoading(false);
    }
  };

  const loginWithPhoneVerificationToken = async (code: string) => {
    resetErrors();
    let ownRefreshToken: string | undefined;

    try {
      setLoading(true);
      const response = await apiVerifyPhone({
        code,
        device: await buildDeviceDetails(),
      });
      ownRefreshToken = await storeAuthResponse(response);
      const userProfile = await validateLogin();
      loginEventEmitter.emit(LOGIN_EVENT, "phone", userProfile);
      setLoginPhoneCountryCode("");
      setLoginPhoneNumber("");
    } catch (e) {
      console.log("[web-login] phone verification login failed:", String(e));
      setLoginError(
        typeof e === "string" || e instanceof ValidationError
          ? e.toString()
          : "Invalid verification code",
      );
      if (ownRefreshToken !== undefined) {
        await endOwnedSession(ownRefreshToken, "login-phone-verify-failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const loginWithAutoLoginToken = async (token: string) => {
    resetErrors();
    // #327: the handoff asserts WHICH member is at this browser (the app
    // minted the token for them), so a stale session from another member must
    // never survive or resurrect under it.
    // #990: but the session is no longer cleared on entry. A stale link
    // reopened in the browser (a restored tab) used to sign out a working
    // session in every tab. Now the session stored at entry is kept until the
    // API has answered.
    const entryRefreshToken = storedRefreshToken();
    // Set once this flow has stored the session the API returned.
    let ownRefreshToken: string | undefined;

    try {
      setLoading(true);
      const response = await apiLoginWithAutoLoginToken({
        token,
        device: await buildDeviceDetails(),
      });
      // #327: clear, then store. No earlier session, this member's or
      // another's, survives a good handoff. The reason keeps its old name: the
      // API's web-logout telemetry only records reasons it lists
      // (LogController WEB_LOGOUT_REASONS) and logs any other as "other".
      await logout(false, "auto-login-entry");
      ownRefreshToken = await storeAuthResponse(response);
      const userProfile = await validateLogin();
      loginEventEmitter.emit(LOGIN_EVENT, "auto", userProfile);
    } catch (e) {
      if (ownRefreshToken !== undefined) {
        await endOwnedSession(ownRefreshToken, "auto-login-failed");
      } else {
        await endEntrySessionUnlessSameMember(token, entryRefreshToken);
      }
      throw e;
    } finally {
      setLoading(false);
    }
  };

  // Ends the session in storage unconditionally. Only for the member's own
  // Log out (logout(true)) and the handoff entry (#327). Every other caller
  // goes through endOwnedSession.
  const logout = async (
    registerLogoutWithApi = false,
    reason = "member-sign-out",
  ) => {
    const deviceId = getUniqueId();

    console.log(`[web-logout] ${reason}`);
    // #856 / WS-12: record which logout() fired, before the keys go.
    queueWebLogoutEvent(reason, "cleared", {
      hadAccessToken: !!localStorage.getItem(AUTH_TOKEN_STORAGE_KEY),
      hadRefreshToken: !!localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY),
    });
    localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);

    try {
      if (registerLogoutWithApi) {
        await apiLogout({ deviceId });
        window.location.reload();
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoggedIn(false);
      setLoading(false);
    }
  };

  // WS-01 (#856, #824): end the session this caller was acting on — and only
  // that one. If storage now holds a different session (another tab logged
  // in, or a newer flow in this tab stored one), this is a complete no-op:
  // no key removal and no signed-out state in this tab, because the stored
  // session is live and this tab's next request will use it.
  const endOwnedSession = async (
    ownedRefreshToken: string | null,
    reason: string,
  ) => {
    if (!isSessionOwnedBy(ownedRefreshToken)) {
      console.log(
        `[web-logout] ${reason}: skipped — storage holds a different session (another tab or a newer login)`,
      );
      queueWebLogoutEvent(reason, "skipped", {
        hadAccessToken: !!localStorage.getItem(AUTH_TOKEN_STORAGE_KEY),
        hadRefreshToken: !!localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY),
      });
      return;
    }
    await logout(false, reason);
  };

  // #990, keeps #327: after the API refuses a handoff link, keep the session
  // that was stored when the handoff started only if it belongs to the member
  // the link names. Otherwise (another member, or either member unreadable)
  // end it, as the entry clear used to. Read from the refresh token: it
  // carries userId and stays in storage while a refresh has dropped ff-at.
  // Through endOwnedSession, so a session another tab stored meanwhile is
  // left alone (WS-01). The entry session counts as still stored if storage
  // holds its token OR another token for the same member: a refresh during
  // the API call rotates the refresh token (client.ts handleUnauthorizedResponse),
  // and that must not let the entry member stay signed in for the link's
  // member (#327). The unchecked token read here can only lead to a clear.
  const endEntrySessionUnlessSameMember = async (
    linkToken: string,
    entryRefreshToken: string | null,
  ) => {
    const linkMember = tokenMemberId(linkToken);
    const entryMember = tokenMemberId(entryRefreshToken);
    if (
      entryRefreshToken !== null &&
      linkMember !== undefined &&
      linkMember === entryMember
    ) {
      console.log(
        "[web-logout] auto-login-failed: kept — the stored session is the member the link names",
      );
      // Listed reason (see loginWithAutoLoginToken); the console line above
      // tells this "kept" apart from endOwnedSession's other-tab "skipped".
      queueWebLogoutEvent("auto-login-failed", "skipped", {
        hadAccessToken: !!localStorage.getItem(AUTH_TOKEN_STORAGE_KEY),
        hadRefreshToken: !!localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY),
      });
      return;
    }
    const storedNow = storedRefreshToken();
    const entrySessionStillStored =
      storedNow === entryRefreshToken ||
      (entryMember !== undefined && tokenMemberId(storedNow) === entryMember);
    await endOwnedSession(
      entrySessionStillStored ? storedNow : entryRefreshToken,
      "auto-login-failed",
    );
  };

  const validateLogin = async (): Promise<UserProfile | null> => {
    if (!localStorage.getItem(AUTH_TOKEN_STORAGE_KEY)) {
      setLoggedIn(false);
      setValidated(true);
      return null;
    }

    const sessionRefreshToken = storedRefreshToken();
    setLoading(true);

    try {
      const user = await apiGetMe();
      setUserProfile(user);
      setLoggedIn(true);

      return user;
    } catch (e) {
      console.log("[web-logout] validateLogin failed:", String(e));
      await endOwnedSession(sessionRefreshToken, "validate-login-failed");
    } finally {
      setLoading(false);
      setValidated(true);
    }

    return null;
  };

  const acceptTerms = async () => {
    setLoading(true);
    if (userProfile) {
      const sessionRefreshToken = storedRefreshToken();
      try {
        const success = await apiAcceptTerms();
        setUserProfile({ ...userProfile, termsAccepted: true });
        return success;
      } catch (e) {
        console.log("[web-logout] acceptTerms failed:", String(e));
        await endOwnedSession(sessionRefreshToken, "accept-terms-failed");
      } finally {
        setLoading(false);
      }
    }

    return false;
  };

  const getVerificationCode = async (req: UpdatePhoneRequest) => {
    setLoading(true);
    setUpdateError(undefined);
    setWaitingOnVerificationCode(false);

    try {
      await getCode({
        countryCode: req.countryCode,
        phoneNumber: req.phoneNumber,
      });

      setWaitingOnVerificationCode(true);
    } catch (e) {
      console.log(e);
      // Guard the message extraction: only a ValidationError carries a
      // usable message; anything else (NoConnectionError, bare Error) gets
      // customer-ready fallback copy instead of `undefined` (#569).
      setUpdateError(
        e instanceof ValidationError
          ? e.toString()
          : "We couldn't send your verification code. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const update = async (req: UpdateUserRequest): Promise<boolean> => {
    setUpdateError(undefined);

    if (!userProfile) {
      setUpdateError("Unauthorised");
      return false;
    }

    try {
      setUpdating(true);
      const updatedUserProfile = await updateMe({
        id: userProfile.id,
        ...req,
      });
      setUserProfile(updatedUserProfile);
      setWaitingOnVerificationCode(false);
      return true;
    } catch (e) {
      if (e.toString().includes("already exists with given phone number")) {
        setUpdateError(
          "Another account already exists with the given phone number. Please contact FarmFLiX Support to resolve this issue.",
        );
      } else if (
        e.toString().includes("User already exists with given email address")
      ) {
        throw new DuplicateEmailError();
      } else {
        setUpdateError(e.toString());
      }

      throw e;
    } finally {
      setUpdating(false);
    }
  };

  const sendFirebaseToken = async (token: string) => {
    if (userProfile) {
      const deviceId = getUniqueId();
      try {
        const success = await apiSendFirebaseToken({
          deviceId,
          userId: userProfile.id,
          token,
        });
        return success;
      } catch (e) {
        console.log("Failed to send Firebase Token: ", e);
      }
    }
    return false;
  };

  // `failedRefreshToken` is the session the 401 handler gave up on (the
  // refresh token whose refresh failed), or null when it had none — see
  // client.ts handleUnauthorizedResponse.
  const onApiUnauthorizedEvent = (
    e?: string | ValidationError<any>,
    failedRefreshToken: string | null = null,
  ) => {
    if (typeof e === "string" || e instanceof ValidationError) {
      setLoginError(e.toString());
    }

    endOwnedSession(failedRefreshToken, "api-unauthorized");
  };

  const resetLoginWithPhone = () => {
    setWaitingOnVerificationCode(false);
    resetErrors();
  };

  const resetErrors = () => {
    setLoading(false);
    setLoginError(undefined);
    setPhoneSuggestion(undefined);
    setUpdateError(undefined);
    setPaymentError(false);
  };

  const resetPhoneSuggestion = () => setPhoneSuggestion(undefined);

  const resetRetryPaymentErrors = () => {
    setRetryPaymentStatus("");
    setRetryPaymentFailureResponse("");
  };

  const updateCard = setUpdatingCard;

  const getMembershipDetails = async (): Promise<MembershipDetails> => {
    return apiGetMembershipDetails();
  };

  const handleRetryPaymentStatus = async (lastFailedPaymentID: string) => {
    try {
      setRetryPaymentStatus("pending");

      const latestPayment = async () => {
        const memberDetails = await getMembershipDetails();
        const paymentDetails = memberDetails.payments[0];
        return paymentDetails;
      };

      // Define retryAttempts
      let retryAttempts = 0;

      const PaymentCallback = async () => {
        const LP = await latestPayment(); // Latest payment entry

        if (LP) {
          // Latest payment id is equal to the last failed payment entry, poll again
          if (LP.id === lastFailedPaymentID) {
            // Increment retry attemps
            retryAttempts = retryAttempts + 1;
            // If retryAttempts reach 5, provide unable to process feedback
            if (retryAttempts === 5) {
              return setRetryPaymentStatus("unable-process");
            }
            // Call again
            setTimeout(PaymentCallback, retryAttempts * 2000);
            // Latest payment id is not equal to the last failed payment entry
          } else if (LP.id !== lastFailedPaymentID) {
            // Latest payment entry status is failure, show error message w/ try again, else show success message
            if (LP.status === "failure") {
              setRetryPaymentFailureResponse(LP.failureReason);
              return setRetryPaymentStatus("failure");
            } else if (LP.status === "success") {
              return setRetryPaymentStatus("success");
            }
          }
        }
      };

      await PaymentCallback();
    } catch (e) {
      setRetryPaymentStatus("");
      console.log("Error: " + e);
    }
  };

  const handleCardSubmit = async (
    paymentMethod: string,
  ): Promise<UpdateStripeCardResponse> => {
    setUpdateError(undefined);

    if (!stripe) {
      return {
        success: false,
        error: "Stripe is not initialized",
      };
    }

    setUpdating(true);
    setUpdatingCard(true);

    try {
      const response = await updateStripeCard({
        id: paymentMethod,
      });

      console.log("updateStripeCard response: ", response);

      if (!response.success) {
        setUpdateError(
          response.error.toString() || "Error updating card details",
        );
      }

      return response;
    } catch (e) {
      setUpdateError(e.toString() || "Error updating card details");

      return {
        success: false,
        error: e.toString() || "Error updating card details",
      };
    } finally {
      setUpdating(false);
      setUpdatingCard(false);
    }
  };

  // Best-effort client-side audit trail for a failed auto-renew change.
  // AccountProvider sits ABOVE LoggerProvider in the tree (LoggerContext
  // consumes AccountContext), so we can't read LoggerContext here — and
  // importing it would drag firebase init into this module. Instead we write
  // straight into the same persisted `ff-log-*` localStorage queue that
  // LoggerContext.flushLogs drains to the backend on its interval, so even an
  // offline failure surfaces server-side for staff follow-up (#567).
  const queueMembershipActionFailure = (
    action: "cancel-membership" | "resume-membership",
    error: any,
  ) => {
    try {
      const timestamp = Date.now();
      const errorMessage =
        typeof error === "string"
          ? error
          : error instanceof Error || error instanceof ValidationError
            ? error.toString()
            : "Unknown error";

      localStorage.setItem(
        `ff-log-${timestamp}-${action}`,
        JSON.stringify({
          userId: userProfile?.id?.toString(),
          category: LogEventCategory.ACCOUNT_ACTION,
          event: action,
          // severity:"error" opts this event into the API's handleErrorAlerts
          // path (LogController): it persists a SystemAlert (the staff-visible
          // "System Alerts" admin page) AND sends the deduped support email —
          // the existing audit precedent, reused rather than rebuilt (#567).
          data: {
            action,
            outcome: "failed",
            error: errorMessage,
            severity: "error",
          },
          timestamp: new Date(),
        }),
      );
    } catch (loggingError) {
      console.log("Failed to queue membership failure log", loggingError);
    }
  };

  const handleCancelMembership = async () => {
    setUpdateError(undefined);

    if (!stripe) {
      return;
    }

    try {
      setUpdating(true);
      // Body is intentionally `{}` — every call site passes no id and the
      // backend derives the member from the bearer token (#567).
      await cancelMembership({} as CancelSubscriptionRequest);

      await new Promise((resolve) => setTimeout(resolve, 3000));
      await validateLogin();
    } catch (e) {
      queueMembershipActionFailure("cancel-membership", e);
      setUpdateError(
        "Whoops! Something went wrong - please contact FarmFLiX Support.",
      );
      throw new Error(
        "Whoops! Something went wrong - please contact FarmFLiX Support.",
      );
    } finally {
      setUpdating(false);
    }
  };

  const handleResumeMembership = async () => {
    setUpdateError(undefined);

    if (!stripe) {
      return;
    }

    try {
      setUpdating(true);
      // Body is intentionally `{}` — see handleCancelMembership (#567).
      await resumeMembership({} as CancelSubscriptionRequest);

      await new Promise((resolve) => setTimeout(resolve, 3000));
      await validateLogin();
    } catch (e) {
      queueMembershipActionFailure("resume-membership", e);
      setUpdateError(
        "Whoops! Something went wrong - please contact FarmFLiX Support.",
      );
      throw new Error(
        "Whoops! Something went wrong - please contact FarmFLiX Support.",
      );
    } finally {
      setUpdating(false);
    }
  };

  const getUpgradePreview = useCallback(async (userId: string) => {
    const previewResponse = await retrieveUpgradePreview({
      id: userId,
    } as CancelSubscriptionRequest);

    if (!previewResponse) {
      console.log("Invoice not found");
      return;
    }

    setUpgradePreviewDetails(previewResponse);
  }, []);

  const handleUpgradeMembership = async (userId: string) => {
    setUpdateError(undefined);

    if (!stripe) {
      return;
    }

    try {
      setUpdating(true);
      console.log("Handle Upgrade Membership...");
      await upgradeMembership({
        id: userId,
      } as CancelSubscriptionRequest);

      await new Promise((resolve) => setTimeout(resolve, 3000));
    } catch (e) {
      setUpdateError(
        "Whoops! Something went wrong - please contact FarmFLiX Support.",
      );
      // throw new Error(
      //   "Whoops! Something went wrong - please contact FarmFLiX Support."
      // );
    } finally {
      setUpdating(false);
    }
  };

  const handleRemovePaymentMethod = async (userId: string) => {
    setUpdateError(undefined);

    if (!stripe) {
      return;
    }

    try {
      setUpdating(true);
      const removePaymentMethod = await removeSavedPaymentMethod({
        id: userId,
      } as CancelSubscriptionRequest);

      if (!removePaymentMethod) {
        setUpdating(false);
        throw new Error(
          "Can't resume subscription at this time - please contact FarmFLiX Support",
        );
      }

      return removePaymentMethod;
    } catch (e) {
      setUpdateError(
        "Whoops! Something went wrong - please contact FarmFLiX Support.",
      );
    } finally {
      setUpdating(false);
    }
  };

  if (!loading && !validated) {
    validateLogin();
  }

  // Subscription Products
  const getSubscriptionProducts = useCallback(async () => {
    const subscriptionProductResponse = await getProducts();

    if (!subscriptionProductResponse) {
      console.log("Subscription products not found");
      return;
    }

    setSubscriptionProducts(subscriptionProductResponse);
  }, []);

  const sendPasswordResetEmail = useCallback(async (email: string) => {
    return generatePasswordReset(email);
  }, []);

  const confirmPasswordReset = useCallback(
    async (req: ConfirmPasswordResetRequest) => {
      await apiConfirmPasswordReset(req);
    },
    [],
  );

  /* Legacy 72H Trial Sign Up */
  const signUpTrial = async ({
    firstName,
    lastName,
    email,
    countryCode,
    phoneNumber,
    password,
  }: SignUpTrialRequest) => {
    try {
      setLoading(true);

      setLoginPhoneCountryCode(countryCode);
      setLoginPhoneNumber(phoneNumber);

      const success = await register({
        firstName,
        lastName,
        email,
        countryCode,
        phoneNumber,
        password,
      });
      if (!success) {
        throw new Error("Can't register at this time");
      } else {
        const phone = phoneNumber;
        const loginSuccess = await apiLoginWithPhone({
          countryCode,
          phone,
          method: "sms",
        });
        if (!loginSuccess) {
          throw new Error("Can't sign in at this time");
        }

        setWaitingOnVerificationCode(true);
      }
    } catch (e) {
      let registerError;
      if (e.toString().includes("User already exists")) {
        registerError =
          "We already know your email address or phone number.  Try logging in instead, and use the reset password option if you can’t remember your password.";
      }
      console.log("[web-signup] trial sign-up failed:", String(e));
      setLoginError(
        typeof registerError === "string" ? e : "Something went wrong!",
      );
      // WS-01: no logout() — this flow stores no session (register + send
      // code only), so there is nothing of its own to end.
    } finally {
      setLoading(false);
    }
  };

  const assignTrialMembership = async (): Promise<string | undefined> => {
    try {
      setLoading(true);

      const products = await getProducts();

      const freeTrialProduct = products.results.find(
        (product) => product.memberStatusId === "trial",
      );

      if (!freeTrialProduct) {
        throw new Error("Free Trial not available at present.");
      }

      // Validate SignIn and ensure locked accounts cannot create a trial.
      const userProfile = await validateLogin();

      if (userProfile) {
        if (userProfile.isLocked || userProfile.memberStatus === "locked") {
          throw new Error("Can't create an account at this time");
        }
      }

      // Create the subscription entitlement for the member
      const createTrialSubscription = await apiCreatePayment({
        productId: freeTrialProduct.id,
      });

      if (!createTrialSubscription) {
        throw new Error("Cannot create a Free Trial at present.");
      }

      // Refresh the profile so the website sees the new trial (the videos
      // page's countdown, Banners/FreeTrialLarge.js, reads
      // subscriptionExpiryDate from it). validateLogin never throws, so a
      // failed refresh can't stop the caller logging the trial.
      await validateLogin();

      // #1261: the website trial (a computer with no smartphone or tablet)
      // lands on the videos page, where the trial countdown is.
      navigate("/videos");

      return createTrialSubscription.payment?.id;
    } catch (e) {
      console.log(e);
      // #659: re-throw so the caller (WebTrialConfirm, #1261) shows the
      // reason in its dialog and skips logMetricEvent. Swallowing it here sent
      // the free_trial / StartTrial pixel events for a trial that was never
      // created, with no matching server event for Meta to pair them with.
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const signUpTrialVerificationToken = async (
    code: string,
  ): Promise<string | undefined> => {
    resetErrors();
    let ownRefreshToken: string | undefined;

    try {
      setLoading(true);
      const response = await apiVerifyPhone({
        code,
        device: await buildDeviceDetails(),
      });
      ownRefreshToken = await storeAuthResponse(response);

      const products = await getProducts();

      const freeTrialProduct = products.results.find(
        (product) => product.memberStatusId === "trial",
      );

      if (!freeTrialProduct) {
        throw new Error("Free Trial not available at present.");
      }

      // Create the subscription entitlement for the member
      const createTrialSubscription = await apiCreatePayment({
        productId: freeTrialProduct.id,
      });

      if (!createTrialSubscription) {
        throw new Error("Cannot create a Free Trial at present.");
      }

      // Validate SignIn
      const userProfile = await validateLogin();
      loginEventEmitter.emit(LOGIN_EVENT, "signup", userProfile);

      // Ensure userProfile is not locked or memberStatus is not set to locked
      if (userProfile) {
        if (userProfile.isLocked || userProfile.memberStatus === "locked") {
          throw new Error("Can't create an account at this time");
        }

        setLoginPhoneCountryCode("");
        setLoginPhoneNumber("");
      }

      navigate("/trialsuccess");

      return createTrialSubscription.payment?.id;
    } catch (e) {
      console.log("[web-signup] trial verification failed:", String(e));
      setLoginError(
        typeof e === "string" || e instanceof ValidationError
          ? e.toString()
          : "Invalid verification code",
      );
      if (ownRefreshToken !== undefined) {
        await endOwnedSession(ownRefreshToken, "signup-verify-failed");
      }
    } finally {
      setLoading(false);
    }
  };

  apiEventEmitter.off(API_EVENT_UNAUTHORIZED);
  apiEventEmitter.on(API_EVENT_UNAUTHORIZED, onApiUnauthorizedEvent);

  const context: IAccountContext = {
    storeAuthResponse,
    createStandardSubscriptionStripe,
    userProfile,
    loggedIn,
    isLocked: userProfile ? userProfile.isLocked : false,
    loading,
    loginError,
    phoneSuggestion,
    resetPhoneSuggestion,
    waitingOnVerificationCode,
    loginWithUsernamePassword,
    loginWithPhone,
    loginWithPhoneVerificationToken,
    loginWithAutoLoginToken,
    logout: () => {
      logout(true, "member-sign-out");
    },
    validateLogin,
    validated,
    acceptTerms,
    sendFirebaseToken,
    resetLoginWithPhone,
    resetErrors,
    update,
    getVerificationCode,
    updateError,
    updating,
    signUpTrial,
    signUpTrialVerificationToken,
    assignTrialMembership,
    getMembershipDetails,
    handleCardSubmit,
    handleRetryPaymentStatus,
    handleCancelMembership,
    handleResumeMembership,
    getUpgradePreview,
    upgradePreviewDetails,
    handleUpgradeMembership,
    handleRemovePaymentMethod,
    updateCard,
    updatingCard,
    paymentError,
    paymentErrorResponse,

    getSubscriptionProducts,
    subscriptionProducts,
    sendPasswordResetEmail,
    confirmPasswordReset,
    loginPhoneCountryCode,
    loginPhoneNumber,

    setRetryPaymentStatus,
    retryPaymentStatus,
    retryPaymentFailureResponse,

    resetRetryPaymentErrors,

    notifyLockedMember,
  };

  return (
    <AccountContext.Provider value={context}>
      {props.children}
    </AccountContext.Provider>
  );
};

export { AccountContext, AccountProvider };
